import { PvaData, usePresignedUrlData } from "@inrange/api-clients";
import { Building } from "@inrange/building-manager-api-client/models-site";
import * as L from "leaflet";
import React, { useEffect, useState } from "react";
import Loading from "../../Loading";
import { PvMapRender } from "../../site/siteSections/PVSystem/PvMap";

interface PvLayoutReviewMapProps {
  siteId: string;
  siteCenter: [number, number];
  buildings: Building[];
  pvaData: PvaData | undefined;
  isFetching: boolean;
  isError: boolean;
  /**
   * When the parent has just kicked off a recompute and is awaiting
   * the new layout, this is true. The recompute query lives in the
   * wrapper above (so it survives row-switches) - we just render the
   * "Recomputing..." state instead of the map until the parent clears
   * the flag.
   */
  isRecomputing: boolean;
  /**
   * Fires when the underlying Leaflet map mounts (and again with
   * undefined on unmount). The per-site item uses this to drive
   * the Y/T zoom keyboard shortcuts.
   */
  onMapReady?: (map: L.Map | undefined) => void;
}

/**
 * Read-only PV layout map for stage 2 of bulk-review. Renders the
 * real PVA-generated panel geometry on top of the site's buildings.
 * Cmd+. / Ctrl+. toggles the panels layer so the operator can compare
 * the layout against the satellite image underneath.
 *
 * Does not own the PVA query - the per-site item mounts it at row
 * level so the layout pre-fetches while the operator is still on
 * stage 1. This component just consumes the resolved data.
 */
const PvLayoutReviewMap: React.FC<PvLayoutReviewMapProps> = ({
  siteId,
  siteCenter,
  buildings,
  pvaData,
  isFetching,
  isError,
  isRecomputing,
  onMapReady,
}) => {
  const [showPv, setShowPv] = useState(true);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "." && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setShowPv((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (isRecomputing) {
    return (
      <div style={overlayStateStyle} data-testid="bulk-review-pv-recomputing">
        <Loading
          height="100%"
          label="PV layout recomputing — the chip will flip to PV pending when this resolves."
        />
      </div>
    );
  }

  if (isError) {
    return (
      <div style={overlayStateStyle}>
        Failed to load PV layout. Try Skip and come back later.
      </div>
    );
  }

  if (!pvaData) {
    return (
      <div style={overlayStateStyle}>
        <Loading height="100%" label="Loading PV layout" />
      </div>
    );
  }

  // If PVA returned panels we render the map regardless of any
  // task descriptor (PVA may attach a task to indicate a refresh
  // is in flight while still serving the previous design). Only
  // when we have neither panels nor a finished design do we fall
  // back to the task-pending / no-panels states.
  if (!pvaData.geojson?.panels_aligned) {
    if (pvaData.task || isFetching) {
      return (
        <div
          style={overlayStateStyle}
          data-testid="bulk-review-pv-task-pending"
        >
          <Loading
            height="100%"
            label="PV layout generating in the background. The map will appear when PVA finishes — usually under a minute."
          />
        </div>
      );
    }
    // The last task terminally failed and PVA will not auto-relaunch it (it
    // only retries on a boundary change or an explicit retry from the site
    // page). Report the failure - and its reason, if PVA recorded one -
    // rather than implying the layout is still on its way.
    if (pvaData.task_metrics?.task_run_success === false) {
      return (
        <div style={overlayStateStyle} data-testid="bulk-review-pv-failed">
          PV generation failed
          {pvaData.task_metrics.error
            ? `: ${pvaData.task_metrics.error}`
            : "."}{" "}
          Open the site to retry.
        </div>
      );
    }
    return (
      <div style={overlayStateStyle}>
        PV layout has no panel data yet. Skip this site and come back later.
      </div>
    );
  }

  return (
    <div
      data-testid="bulk-review-pv-map"
      style={{ position: "relative", width: "100%", height: "100%" }}
    >
      <PvMapRender
        siteId={siteId}
        siteCenter={siteCenter}
        buildings={buildings}
        panelsUrl={pvaData.geojson?.panels_aligned}
        customObjects={pvaData.custom_objects ?? []}
        siteImage={pvaData.site_image}
        usePresignedUrlData={usePresignedUrlData}
        showPanels={showPv}
        onMapReady={onMapReady}
        hideSatelliteCachedNotice={true}
      />
      <div style={hintStyle} data-testid="bulk-review-pv-toggle-hint">
        {showPv ? "PV layout visible" : "PV layout hidden"} · ⌘. / Ctrl+. to
        toggle
      </div>
    </div>
  );
};

const overlayStateStyle: React.CSSProperties = {
  width: "100%",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  color: "#495057",
  textAlign: "center",
  padding: 16,
};

const hintStyle: React.CSSProperties = {
  position: "absolute",
  bottom: 8,
  right: 8,
  padding: "4px 8px",
  backgroundColor: "rgba(0, 0, 0, 0.55)",
  color: "white",
  borderRadius: 4,
  fontSize: 11,
  pointerEvents: "none",
};

export default PvLayoutReviewMap;
